import type { DialogueLineSpeaker, TelopLineSpeaker } from '../../../../features/scenarios/types';

/**
 * 読み取り表示 (DialogueLinesView / TelopLinesView) で「同一話者が連続する行」を
 * 1 つのグループにまとめるための共通ロジック。話者チップ (顔写真) をグループごとに
 * 1 枚だけ出し、文言はその右に縦に並べる (2026-09-18 ユーザー指示)。
 *
 * 同一判定はチップの解決と同じ粒度: 俳優は `actor_kind:actor_id`、登場人物宣言は
 * `character:key`、それ以外の呼称は trim した label 文字列。編集モードやデータの形は
 * 変えない (表示上のまとめだけ)。
 */
export interface SpeakerLineGroup<L> {
  /** 同一話者の判定キー (テスト・data 属性用)。 */
  key: string;
  /** グループ先頭行の元配列上の添字。 */
  firstIndex: number;
  lines: L[];
}

export function groupConsecutiveLines<L>(lines: L[], keyOf: (line: L) => string): Array<SpeakerLineGroup<L>> {
  const groups: Array<SpeakerLineGroup<L>> = [];
  lines.forEach((line, idx) => {
    const key = keyOf(line);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.lines.push(line);
    } else {
      groups.push({ key, firstIndex: idx, lines: [line] });
    }
  });
  return groups;
}

/** セリフ行の話者同一判定キー。 */
export function dialogueSpeakerGroupKey(speaker: DialogueLineSpeaker): string {
  if (speaker.kind === 'narration') return 'narration';
  if (speaker.actor_id && speaker.actor_kind) return `${speaker.actor_kind}:${speaker.actor_id}`;
  if (speaker.character_key) return `character:${speaker.character_key}`;
  return `label:${(speaker.label ?? '').trim()}`;
}

/** テロップ行の話者同一判定キー (話者なしは `none`、ナレーションは `narration`)。 */
export function telopSpeakerGroupKey(speaker: TelopLineSpeaker | null): string {
  if (!speaker) return 'none';
  if (speaker.kind === 'narration') return 'narration';
  if (speaker.kind === 'actor' && speaker.actor_id && speaker.actor_kind) return `${speaker.actor_kind}:${speaker.actor_id}`;
  if (speaker.character_key) return `character:${speaker.character_key}`;
  return `label:${(speaker.label ?? '').trim()}`;
}

/**
 * 写真の無い話者チップに重ねる頭文字 (先頭 1 文字。サロゲートペア/結合文字を壊さないよう
 * Array.from で切る。英字は大文字化)。名前が空なら空文字 (= 頭文字を出さない)。
 */
export function speakerInitial(name: string | null | undefined): string {
  const first = Array.from((name ?? '').trim())[0];
  return first ? first.toLocaleUpperCase() : '';
}
